/**
 * Consulting & Strategy vertical — every user-visible string in the sprint that NAMES this domain.
 *
 * Like `data-analytics-copy.ts` (and unlike finance's), nothing here is re-exported from a shipped
 * `_data` layer: this vertical has no prior life as a standalone sprint, so this file IS the source of
 * truth for its copy. The scope rule from `../_engine/types.ts` still holds — if swapping the vertical
 * would change the string, it lives here; invariant chrome ("Back", "Continue", "Module 2 of 4", the
 * swipe stamps) stays in the engine.
 *
 * The register is the other packs': plain, concrete, a little opinionated, never markety. Where
 * finance says "income statement" and data says "funnel", this says "operating table" — the numbers a
 * business runs on, read for whether the operation is keeping up with its own growth.
 */

import type { LabsCopy } from '../_engine/types';

/**
 * The primer's row operators. Finance's artifact is arithmetic (`− COGS`, `= Gross Profit`) and data's
 * funnel rows are survivors of a step (`↓`). An operating table is NEITHER: its rows are independent
 * gauges on the same machine — demand, quality, friction, failure — and no row derives from the one
 * above it. An empty operator is the only honest one; `−` or `↓` would teach a false mental model.
 */
const PRIMER_OPERATORS: readonly string[] = ['', '', '', ''];

/** The primer's four rows, in the order an operator reads them. Paired to {@link PRIMER_OPERATORS} by index. */
const PRIMER_ROWS: readonly { hint: string; label: string }[] = [
  { hint: 'The demand coming in the door', label: 'Orders' },
  { hint: 'The promise the business kept', label: 'On-Time Deliveries' },
  { hint: 'The friction growth creates', label: 'Support Tickets' },
  { hint: 'The failures the business paid for', label: 'Refunds' },
];

export const CONSULTING_COPY: LabsCopy = {
  m1: {
    activity: {
      eyebrow: 'Module 1 · A Day in the Life',
      helper: 'Eight activities from a real consulting day',
    },
    complete: {
      sub: 'Good start. One module down, three to go.',
    },
    intro: {
      accentEm: 'would I want to do this every day?',
      accentPre: "You'll see {count} real activities, one at a time. For each one, ask yourself:",
      headline: 'A day in the life',
      lead: 'Consulting careers split into two worlds. Strategists work out what the business should do next — the analysis, the sizing, the recommendation someone senior has to act on. Operators make the business actually work — the process, the rollout, the fix that survives contact with a real team. The day-to-day work looks very different.',
    },
    results: {
      bridge: {
        emphasis: 'think',
        post: " when the problem is messy. That's the other half of your archetype.",
        pre: 'Next: 4 questions about how you ',
      },
      /**
       * Shown INSTEAD of the split reading when the learner swiped right on nothing.
       *
       * Without it, `computeLean` has no signal, returns a dead-even 50/50, and the split copy tells
       * someone who rejected all eight days that "both halves pulled you on different days" above two
       * bars reading 0 and 0. Found by a design review on 2026-08-26.
       */
      noneChosen:
        'You passed on all eight. That is real information, not a failed quiz: those are the days, and none of them appealed. Consulting may not be your thing, and the useful question now is which kind of work is.',
      interpretation: {
        moderateA:
          'A modest lean toward strategy. The recommendation side pulled you, but the making-it-happen side still got a few yeses.',
        moderateB:
          'A modest lean toward operations. The fixing pulled you, but you stayed open to the work of deciding what to fix.',
        split:
          "You're genuinely split. Both halves of the job pulled you on different days. That's a signal, not indecision. The next module sharpens which one fits how you actually think.",
        strongA:
          "A clear lean toward strategy. You're drawn to the question side: what should this business do, and what would make a smart person believe it.",
        strongB:
          "A clear lean toward operations. You're drawn to the doing side: the process that broke, the rollout that stuck, the fix that holds after you leave the room.",
      },
    },
  },
  m2: {
    complete: {
      sub: "Halfway. You're reading businesses like an advisor now.",
    },
    intro: {
      headline: 'Your mindset, in 4 situations',
      lead: "What you'd do is one signal. How you reason is the other.",
      points: [
        '4 real situations a consultant faces',
        'Two reasonable instincts on each. Pick the one that fits you',
        'No right answers, only honest ones',
      ],
    },
    results: {
      footer: 'This will appear on your analysis at the end.',
    },
  },
  m3: {
    activity: {
      diagnosisHeading: 'Diagnose in one sentence',
      diagnosisPlaceholder: "In plain English, what is Swiftcart's biggest problem?",
      diagnosisSample:
        'Orders more than tripled but every quality rate moved the wrong way, so Swiftcart is scaling its problems faster than its operation. The constraint is not demand, it is delivery — and more growth makes it worse, not better.',
      eyebrow: 'Module 3 · Read the operation',
      feedbackCorrect: 'Nailed it.',
      feedbackWrong: 'Not quite. Try another.',
      metricsHeading: 'Pick the Right Rate for FY2024',
      metricsHelper: 'Three rates. Each has one correct answer. Pick from the options.',
      // The payoff of M3: the topline explodes while every quality gauge decays in lockstep. Every
      // percentage here is computed off the table in `consulting.ts` — on-time 108/120, 178/210,
      // 304/380; tickets per order 9.6/120 → 45.6/380; refunds 3.6/120 → 19/380. Changing a cell
      // there means re-deriving this paragraph.
      revealBody:
        'The topline is spectacular: orders more than tripled in two years (+217%). Now read the gauges underneath. On-time delivery fell 90.0% → 84.8% → 80.0%. Support tickets per order climbed 8% → 10% → 12%. Refunds — the failures Swiftcart PAYS for — went 3.0% → 4.0% → 5.0% of all orders. Every quality rate decays at almost exactly the pace the order book grows, which means the operation is not straining under growth, it is being outrun by it. One in five deliveries now breaks a promise, and each of those costs a ticket, a refund, or a customer. Growth is not the cure here; unfixed, it is the accelerant. Working out whether a business needs more demand or a better machine is exactly what consultants get paid to do.',
      revealLabel: 'The problem:',
      subhead:
        "A fictional delivery startup, a quick warm-up before the real thing. Same shape you'd see in any operations review.",
      tableTag: 'Practice example',
    },
    complete: {
      sub: 'One more module, then your real analysis.',
    },
    intro: {
      chips: ['A 30-second primer on how it works', "Then you'll read three rates off a real one"],
      headline: 'Read the operation',
      subtitle: "Every consulting engagement starts with one artifact: the client's operating numbers.",
    },
    primer: {
      // Load-bearing shape: the engine italicises everything after the first ": ", so the thesis must
      // keep its colon — the clause after it is the sentence the learner should remember.
      body: 'An operating table answers one question: as the business grows, is it getting better at the job, or just bigger?',
      eyebrow: 'Module 3 · Read the operation',
      headline: "What's an operating table?",
      pullQuote: 'Growth hides operational decay until it stops hiding it.',
      rows: PRIMER_ROWS.map((row, i) => ({
        hint: row.hint,
        label: row.label,
        operator: PRIMER_OPERATORS[i] ?? '',
      })),
    },
  },
  // Mirrors the other verticals' offer structure — provoke, then promise, then say concretely what 15
  // minutes buys. "Read a real company's operating numbers" is the consulting equivalent of finance's
  // "read the numbers": the concrete, unglamorous skill the sprint actually hands over.
  // The post-sprint hand-off, shown only to a learner who has NOT paid (PR #2766). The artifact name is
  // the pack's own: finance produces a company valuation, data a funnel analysis, and this sprint an
  // operations analysis — the same phrase `welcome.lead` already promises, so the payoff names back
  // exactly what the front door offered. The body is invariant across verticals because it describes the
  // externship and the trial, not the track.
  handoff: {
    artifactTitle: 'Operations analysis',
    body: 'That was {duration} of the real thing. The full externship runs six weeks with a real company and a certificate at the end. Your free trial starts it.',
    duration: '15 minutes',
    headline: 'You just finished your first Consulting & Strategy project',
    trackLabel: 'Consulting & Strategy track',
  },
  offer: {
    body: "15 minutes, start to finish: learn to read a real company's operating numbers, then analyze a business you already use & walk away with work worth sharing",
    eyebrow: 'Consulting & Strategy Track',
    headlineLead: 'Stop wondering if consulting is for you.',
    headlineTail: 'Spend 15 minutes finding out.',
    tilePromise: "Find the story inside a real company's operations",
  },
  // The PRE-paywall offer. Deliberately NOT the catalog `offer` block above: "try the real work, free,
  // before you pay" only means anything to someone who has not paid, and the two surfaces must never
  // read as the same pitch twice. The provocation and the price frame are invariant across verticals;
  // only the body names this track back to the learner who declared it.
  prepaywallOffer: {
    // "Strategy & Consulting", NOT the vertical's display name. This sentence QUOTES the learner's own
    // application answer back to them, and the option they picked is `Strategy & Consulting`
    // (form-step-8.tsx) — the same string `careerFocusArea` gates on. The eyebrow above may carry the
    // vertical's brand name in either order, but a sentence beginning "You told us" has to match what
    // they actually told us or it reads as being about a different track.
    body: "You told us Strategy & Consulting is your track. This 15-minute exercise is the actual work: read a real company's operating numbers, find where the business breaks, keep what you make.",
    eyebrow: 'Consulting & Strategy Track',
    headlineLead: 'Try the real work, free,',
    headlineTail: 'before you pay.',
  },
  m4: {
    activity: {
      feedbackWrong: 'Not the strongest read. Try another.',
      nameHeading: 'Your name for the analysis',
      pickHeading: 'Pick your read',
      pickHelper: "After reading the analysis, pick the read you'd give.",
      sampleFooter: 'Want to go deeper? Push back on this read with your own analysis.',
      sampleHeading: 'Read the sample analysis',
      sampleHelper: 'A curated 3-bullet read on this company — editorial, not a live model. Takes about 8 seconds.',
      samplePanelLabel: 'Sample analysis · curated example',
      sampleRunCta: 'Run analysis',
      submitCta: 'See my analysis',
      takeHeading: 'Where do you agree with the analysis, and where do you see it differently?',
      takeHelper: 'Consultants push back; prompt-followers just paste. Be specific.',
      takePlaceholder: 'In one or two sentences: what did the analysis miss, or what would you push back on?',
    },
    intro: {
      // The LAST paragraph renders as the accent paragraph — order is load-bearing.
      body: [
        "Swiftcart was practice. Now you'll pick a company you have almost certainly used, read three years of the operating numbers it actually reports to its investors, and form your own view alongside one we generated for you.",
        "The point isn't to agree with it. It's to build your own read and notice exactly where the two diverge. That gap is the job.",
      ],
      headline: 'Your first real operations analysis',
    },
    selection: {
      eyebrow: 'Module 4 · Real operations analysis',
      headline: 'Pick a company',
      subhead:
        'Each comes with three years of real operating numbers, straight from the company. No NDA needed: these companies publish them every quarter.',
    },
  },
  recommendations: {
    archLineNoSubject: "You're {archetype}. Here's where that takes you next.",
    archLineWithSubject: "You're {archetype}. Here's where your {subject} analysis takes you next.",
  },
  welcome: {
    cta: 'Begin',
    // The pill's trailing "· Discovery experience" is invariant chrome (every vertical is one), so it
    // stays in the engine; only the track name is this vertical's to supply.
    eyebrow: 'Consulting & Strategy Track',
    headlineLead: 'What does a consulting career actually look like?',
    headlineTail: 'Spend 15 minutes finding out.',
    lead: 'Two real career paths. Your first operations analysis.',
    metaDuration: 'About 15 minutes',
    steps: [
      { sub: 'See how two consulting paths spend a day', title: 'Meet two consultants' },
      { sub: 'Quick reads reveal how you think', title: 'Discover your mindset' },
      { sub: 'Read the numbers behind a company you use', title: 'Analyze a real business' },
    ],
  },
};
